import React, { useState, useEffect } from 'react';

import { useParams, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import jobService from '../../../services/jobService';
import publicJobService from '../../../services/publicJobService';
import candidateService from '../../../services/candidateService';
import { processUploadedFiles } from '../../../services/documentProcessingService';
import PublicHeader from '../../../components/ui/PublicHeader';
import PublicFooter from '../../../components/ui/PublicFooter';
import { useAuth } from '../../../contexts/AuthContext';

const JobApplicationForm = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isCandidate } = useAuth();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    candidate_name: '',
    candidate_email: '',
    candidate_phone: '',
    cover_letter: '',
    cv_file: null,
    cv_text: '',
    consent_data_processing: false,
    consent_communication: false
  });

  const [cvProcessing, setCvProcessing] = useState({
    status: 'idle', // idle, processing, completed, failed
    progress: 0,
    message: ''
  });

  useEffect(() => {
    loadJobDetails();
  }, [jobId]);

  // Auto-fill form with authenticated user data
  useEffect(() => {
    const autoFillForm = async () => {
      if (isAuthenticated && isCandidate && user) {
        // Start with user data
        const autoFilledData = {
          candidate_name: user.name || '',
          candidate_email: user.email || '',
          candidate_phone: '',
          cover_letter: '',
          cv_file: null,
          cv_text: '',
          consent_data_processing: false,
          consent_communication: false
        };

        // Try to get more detailed candidate profile
        try {
          const profile = await candidateService.getProfile();
          if (profile && profile.candidate) {
            autoFilledData.candidate_name = profile.candidate.name || autoFilledData.candidate_name;
            autoFilledData.candidate_email = profile.candidate.email || autoFilledData.candidate_email;
            autoFilledData.candidate_phone = profile.candidate.phone || autoFilledData.candidate_phone;
          }
        } catch (error) {
          console.log('Could not fetch detailed candidate profile, using basic user data');
        }

        setFormData(autoFilledData);
      }
    };

    autoFillForm();
  }, [isAuthenticated, isCandidate, user]);

  const loadJobDetails = async () => {
    try {
      setLoading(true);
      const response = await publicJobService.getPublicJob(jobId);
      
      // Only allow applications to active jobs
      if (response.job.status !== 'active') {
        navigate('/public/jobs', { 
          state: { error: 'This job is no longer accepting applications.' }
        });
        return;
      }
      
      setJob(response.job);
    } catch (error) {
      console.error('Error loading job:', error);
      navigate('/public/jobs', { 
        state: { error: 'Job not found or no longer available.' }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf', 'application/msword', 
                         'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

    if (file.size > maxSize) {
      setErrors(prev => ({ ...prev, cv_file: 'File size must be less than 10MB' }));
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, cv_file: 'Please upload a PDF or Word document' }));
      return;
    }

    setFormData(prev => ({ ...prev, cv_file: file }));
    setErrors(prev => ({ ...prev, cv_file: null }));

    // Extract text from CV
    try {
      setCvProcessing({ status: 'processing', progress: 50, message: 'Processing CV...' });
      
      const processedFiles = await processUploadedFiles([file]);
      const extractedText = processedFiles[0]?.extractedText;

      if (!extractedText || extractedText.trim().length < 50) {
        throw new Error('Could not extract readable text from CV. Please ensure your CV contains selectable text.');
      }

      setFormData(prev => ({ ...prev, cv_text: extractedText }));
      setCvProcessing({ 
        status: 'completed', 
        progress: 100, 
        message: 'CV processed successfully!' 
      });
      
    } catch (error) {
      console.error('CV processing error:', error);
      setCvProcessing({ 
        status: 'failed', 
        progress: 0, 
        message: error.message || 'Failed to process CV' 
      });
      setErrors(prev => ({ ...prev, cv_file: error.message }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.candidate_name.trim()) {
      newErrors.candidate_name = 'Full name is required';
    }

    if (!formData.candidate_email.trim()) {
      newErrors.candidate_email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.candidate_email)) {
      newErrors.candidate_email = 'Please enter a valid email address';
    }

    if (!formData.cv_file) {
      newErrors.cv_file = 'Please upload your CV';
    }

    if (!formData.cv_text.trim()) {
      newErrors.cv_text = 'CV text could not be extracted. Please try a different file.';
    }

    if (!formData.consent_data_processing) {
      newErrors.consent_data_processing = 'You must consent to data processing to apply';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector('.text-red-600');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setSubmitting(true);

    try {
      // Submit application through backend
      const applicationData = {
        jobId: jobId,
        candidateName: formData.candidate_name,
        candidateEmail: formData.candidate_email,
        candidatePhone: formData.candidate_phone || null,
        cvText: formData.cv_text,
        coverLetter: formData.cover_letter || null,
        autoProcessCv: true // Enable AI processing
      };

      const result = await publicJobService.submitApplication(applicationData);
      
      console.log('Application submitted successfully:', result);
      setSubmitted(true);
      
      // Scroll to success message
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);

    } catch (error) {
      console.error('Application submission failed:', error);
      setErrors({ 
        submit: error.message || 'Failed to submit application. Please try again.' 
      });
      
      // Scroll to error
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } finally {
      setSubmitting(false);
    }
  };

  const formatSalary = (min, max, currency = 'USD') => {
    if (!min && !max) return null;
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    
    if (min && max) {
      return `${formatter.format(min)} - ${formatter.format(max)}`;
    } else if (min) {
      return `${formatter.format(min)}+`;
    } else {
      return `Up to ${formatter.format(max)}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={32} className="animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Icon name="Briefcase" size={20} color="white" />
                </div>
                <h1 className="text-xl font-bold text-gray-900">HR AI Careers</h1>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/public/jobs')}
                iconName="ArrowLeft"
              >
                Back to Jobs
              </Button>
            </div>
          </div>
        </header>

        {/* Success Message */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircle" size={32} color="#22c55e" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Application Submitted Successfully!
            </h2>
            
            <div className="bg-white rounded-lg border p-6 text-left mb-8">
              <h3 className="font-semibold text-gray-900 mb-4">What happens next?</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Cpu" size={14} color="#3b82f6" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">AI Analysis</p>
                    <p className="text-sm text-gray-600">Our AI system is analyzing your CV against the job requirements.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="UserCheck" size={14} color="#f59e0b" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">HR Review</p>
                    <p className="text-sm text-gray-600">Our HR team will review your application and AI assessment.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Mail" size={14} color="#22c55e" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">We'll Contact You</p>
                    <p className="text-sm text-gray-600">You'll receive an email update within 3-5 business days.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <Button
                variant="default"
                onClick={() => navigate('/public/jobs')}
                iconName="Briefcase"
                className="w-full sm:w-auto"
              >
                Browse More Jobs
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
        <PublicHeader />
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Icon name="Briefcase" size={20} color="white" />
              </div>
              <h1 className="text-xl font-bold text-gray-900">HR AI Careers</h1>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/public/jobs')}
              iconName="ArrowLeft"
            >
              Back to Jobs
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {errors.submit && (
          <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} color="#ef4444" />
              <p className="text-red-800 font-medium">Application Failed</p>
            </div>
            <p className="text-red-700 mt-1">{errors.submit}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Job Details Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border p-6 sticky top-8">
              <h3 className="font-semibold text-gray-900 mb-4">Job Details</h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-medium text-gray-900">{job?.title}</h4>
                  <p className="text-gray-600">{job?.department}</p>
                </div>

                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Icon name="MapPin" size={14} />
                  <span>{job?.location}</span>
                </div>

                {job?.remote_work_allowed && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Icon name="Wifi" size={14} />
                    <span>Remote Work Available</span>
                  </div>
                )}

                {formatSalary(job?.salary_range_min, job?.salary_range_max, job?.currency) && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Icon name="DollarSign" size={14} />
                    <span>{formatSalary(job?.salary_range_min, job?.salary_range_max, job?.currency)}</span>
                  </div>
                )}

                <div className="pt-4 border-t">
                  <h5 className="font-medium text-gray-900 mb-2">Requirements</h5>
                  {job?.requirements && Array.isArray(job.requirements) ? (
                    <ul className="space-y-1 text-sm text-gray-600">
                      {job.requirements.slice(0, 3).map((req, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <Icon name="Check" size={12} className="text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* Application Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Apply for this Position</h2>
                <p className="text-gray-600">
                  Fill out the form below and upload your CV. Our AI system will analyze your application automatically.
                </p>
                {isAuthenticated && isCandidate && (
                  <p className="text-sm text-blue-600 mt-2">
                    <Icon name="User" size={16} className="inline mr-1" />
                    Form pre-filled with your profile information. Please review and update as needed.
                  </p>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="candidate_name"
                        value={formData.candidate_name}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.candidate_name ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Enter your full name"
                      />
                      {errors.candidate_name && (
                        <p className="text-red-600 text-sm mt-1">{errors.candidate_name}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="candidate_email"
                        value={formData.candidate_email}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.candidate_email ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="your.email@example.com"
                      />
                      {errors.candidate_email && (
                        <p className="text-red-600 text-sm mt-1">{errors.candidate_email}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="candidate_phone"
                      value={formData.candidate_phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                {/* CV Upload */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Upload Your CV</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CV/Resume *
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="cv-upload"
                      />
                      <label htmlFor="cv-upload" className="cursor-pointer">
                        <Icon name="Upload" size={32} className="text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-600 mb-1">
                          Click to upload or drag and drop your CV
                        </p>
                        <p className="text-sm text-gray-500">
                          PDF, DOC, or DOCX (max 10MB)
                        </p>
                      </label>
                      
                      {formData.cv_file && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center justify-center space-x-2">
                            <Icon name="FileText" size={16} className="text-blue-600" />
                            <span className="text-sm text-blue-800 font-medium">
                              {formData.cv_file.name}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {cvProcessing.status !== 'idle' && (
                      <div className="mt-3">
                        <div className={`p-3 rounded-lg ${
                          cvProcessing.status === 'processing' ? 'bg-blue-50' :
                          cvProcessing.status === 'completed' ? 'bg-green-50' : 'bg-red-50'
                        }`}>
                          <div className="flex items-center space-x-2">
                            {cvProcessing.status === 'processing' && (
                              <Icon name="Loader" size={16} className="animate-spin text-blue-600" />
                            )}
                            {cvProcessing.status === 'completed' && (
                              <Icon name="CheckCircle" size={16} className="text-green-600" />
                            )}
                            {cvProcessing.status === 'failed' && (
                              <Icon name="AlertCircle" size={16} className="text-red-600" />
                            )}
                            <span className={`text-sm font-medium ${
                              cvProcessing.status === 'processing' ? 'text-blue-800' :
                              cvProcessing.status === 'completed' ? 'text-green-800' : 'text-red-800'
                            }`}>
                              {cvProcessing.message}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {errors.cv_file && (
                      <p className="text-red-600 text-sm mt-1">{errors.cv_file}</p>
                    )}
                  </div>
                </div>

                {/* Cover Letter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cover Letter
                  </label>
                  <textarea
                    name="cover_letter"
                    value={formData.cover_letter}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Tell us why you're interested in this position..."
                  />
                </div>

                {/* Consent Checkboxes */}
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900">Consent & Privacy</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        name="consent_data_processing"
                        checked={formData.consent_data_processing}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                        id="consent-processing"
                      />
                      <label htmlFor="consent-processing" className="text-sm text-gray-700">
                        I consent to the processing of my personal data for recruitment purposes. *
                      </label>
                    </div>
                    {errors.consent_data_processing && (
                      <p className="text-red-600 text-sm ml-7">{errors.consent_data_processing}</p>
                    )}
                    
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        name="consent_communication"
                        checked={formData.consent_communication}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                        id="consent-communication"
                      />
                      <label htmlFor="consent-communication" className="text-sm text-gray-700">
                        I agree to receive updates about my application via email.
                      </label>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6 border-t">
                  <Button
                    type="submit"
                    disabled={submitting || cvProcessing.status === 'processing'}
                    className="w-full"
                    iconName={submitting ? "Loader" : "Send"}
                    iconClassName={submitting ? "animate-spin" : ""}
                  >
                    {submitting ? 'Submitting Application...' : 'Submit Application'}
                  </Button>
                  
                  <p className="text-xs text-gray-500 text-center mt-3">
                    By submitting this application, you agree to our privacy policy and terms of service.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
         <PublicFooter />
    </div>
  );
};

export default JobApplicationForm;